/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { performance } from "@fluid-internal/client-utils";
import { assert } from "@fluidframework/core-utils/internal";
import { roundToDecimalPlaces } from "./mathTools.js";
/**
 * Helper class that executes a specified code block and writes an
 * {@link @fluidframework/core-interfaces#ITelemetryPerformanceEvent} to a specified logger every time a specified
 * number of executions is reached (or when the class is disposed).
 *
 * @remarks
 * The `duration` field in the telemetry event this class generates is the duration of the latest execution (sample)
 * of the specified code block.
 * See the documentation of the `includeAggregateMetrics` parameter for additional details that can be included.
 *
 * @typeParam TMeasurementReturn - The return type (in a vacuum) of the code block that will be measured, ignoring
 * any custom metric data that might be required by this class. E.g., the code might just return a boolean.
 * @typeParam TCustomMetrics - A type that contains the custom properties that will be used by an instance of this class
 * for custom metrics. Each property in this type should be a number.
 *
 * @internal
 */
export class SampledTelemetryHelper {
    /**
     * {@inheritDoc @fluidframework/core-interfaces#IDisposable.disposed}
     */
    get disposed() {
        return this._disposed;
    }
    /**
     * @param eventBase -
     * Custom properties to include in the telemetry performance event when it is written.
     * @param logger -
     * The logger to use to write the telemetry performance event.
     * @param sampleThreshold -
     * Telemetry performance events will be generated every time we hit this many executions of the code block.
     * @param includeAggregateMetrics -
     * If set to `true`, the telemetry performance event will include aggregated metrics (total duration, min duration,
     * max duration) for all the executions in between generated events.
     * @param perBucketProperties -
     * Map of strings that represent different buckets (which can be specified when calling the 'measure' method), to
     * properties which should be added to the telemetry event for that bucket. If a bucket being measured does not
     * have an entry in this map, no additional properties will be added to its telemetry events. The following keys are
     * reserved for use by this class: "duration", "count", "totalDuration", "minDuration", "maxDuration". If any of
     * them is specified as a key in one of the ITelemetryBaseProperties objects in this map, that key-value pair will be
     * ignored.
     */
    constructor(eventBase, logger, sampleThreshold, includeAggregateMetrics = false, perBucketProperties = new Map()) {
        this.eventBase = eventBase;
        this.logger = logger;
        this.sampleThreshold = sampleThreshold;
        this.includeAggregateMetrics = includeAggregateMetrics;
        this.perBucketProperties = perBucketProperties;
        this._disposed = false;
        this.measurementsMap = new Map();
    }
    /**
     * Executes the specified code and keeps track of execution time statistics.
     * When it's been called enough times (the sampleThreshold for the class) then it generates a log message with the
     * necessary information.
     *
     * @remarks It's the responsibility of the caller to ensure that the same same set of custom metric properties is
     * provided each time this method is called on a given instance of {@link SampledTelemetryHelper}.
     * Otherwise the final measurements in the telemetry event may not be accurate.
     *
     * @param codeToMeasure - The code to be executed and measured.
     * @param bucket - A key to track executions of the code block separately.
     * Each different value of this parameter has a separate set of executions and metrics tracked by the class.
     * If no such distinction needs to be made, do not provide a value.
     * @returns Whatever the passed-in code block returns.
     */
    measure(codeToMeasure, bucket = "") {
        const start = performance.now();
        const returnValue = codeToMeasure();
        const duration = performance.now() - start;
        let loggerData = this.measurementsMap.get(bucket);
        if (loggerData === undefined) {
            loggerData = {
                measurements: { count: 0, duration: -1 },
                dataSums: {},
                dataMaxes: {},
            };
            this.measurementsMap.set(bucket, loggerData);
        }
        const m = loggerData.measurements;
        m.count++;
        m.duration = duration;
        if (this.includeAggregateMetrics) {
            m.totalDuration = (m.totalDuration ?? 0) + duration;
            m.minDuration = Math.min(m.minDuration ?? duration, duration);
            m.maxDuration = Math.max(m.maxDuration ?? 0, duration);
        }
        if (this.isCustomData(returnValue)) {
            loggerData = this.accumulateCustomData(returnValue.customData, loggerData);
        }
        if (m.count >= this.sampleThreshold) {
            // Computed separately to avoid multiple division operations.
            if (this.includeAggregateMetrics) {
                m.averageDuration = (m.totalDuration ?? 0) / m.count;
            }
            this.flushBucket(bucket);
        }
        return returnValue;
    }
    isCustomData(data) {
        return (typeof data === "object" &&
            data !== null &&
            "customData" in data &&
            typeof data.customData === "object");
    }
    accumulateCustomData(customData, loggerData) {
        for (const [key, val] of Object.entries(customData)) {
            assert(typeof key === "string", 0x9df /* Key should be a string */);
            assert(typeof val === "number", 0x9e0 /* Value should be a number */);
            loggerData.dataSums[key] = (loggerData.dataSums[key] ?? 0) + val;
            loggerData.dataMaxes[key] = Math.max(loggerData.dataMaxes[key] ?? Number.NEGATIVE_INFINITY, val);
        }
        return loggerData;
    }
    processCustomData(loggerData, count) {
        const processedCustomData = {};
        if (loggerData.dataSums === undefined || loggerData.dataMaxes === undefined) {
            return processedCustomData;
        }
        const dataSums = loggerData.dataSums;
        const dataMaxes = loggerData.dataMaxes;
        for (const [key, val] of Object.entries(dataSums)) {
            // implementation of class guarantees the keys between dataMaxes and dataSums align.
            processedCustomData[`avg_${key}`] = roundToDecimalPlaces(val / count, 6);
            processedCustomData[`max_${key}`] = dataMaxes[key] ?? 0;
        }
        return processedCustomData;
    }
    flushBucket(bucket) {
        const loggerData = this.measurementsMap.get(bucket);
        if (loggerData === undefined) {
            return;
        }
        const measurements = loggerData.measurements;
        const processedCustomData = this.processCustomData(loggerData, measurements.count);
        if (measurements.count !== 0) {
            const bucketProperties = this.perBucketProperties.get(bucket);
            const telemetryEvent = {
                ...this.eventBase,
                ...bucketProperties, // If the bucket doesn't exist and this is undefined, things work as expected
                ...measurements,
                ...processedCustomData,
            };
            this.logger.sendPerformanceEvent(telemetryEvent);
            this.measurementsMap.delete(bucket);
        }
    }
    dispose(error) {
        for (const [k] of this.measurementsMap.entries()) {
            this.flushBucket(k);
        }
        this._disposed = true;
    }
}
//# sourceMappingURL=sampledTelemetryHelper.js.map